import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getPassengerData, getPassengerToken } from "../../passengerAuth";
import {
    Car, Ambulance, Home, MapPin, Navigation, Clock, CheckCircle,
    XCircle, AlertCircle, Loader, ArrowRight, DollarSign, User, Star
} from "lucide-react";
import "./Passenger.css";

const SERVICE_TYPE_MAP = {
    Emergency: { label: "Emergency", icon: <Ambulance size={16} />, color: "#dc2626", bg: "#fef2f2" },
    Scheduled: { label: "Scheduled Booking", icon: <Clock size={16} />, color: "#0ea5e9", bg: "#e0f2fe" },
    HomeCare: { label: "Home Care", icon: <Home size={16} />, color: "#16a34a", bg: "#f0fdf4" },
    default: { label: "Non-Emergency Service", icon: <Car size={16} />, color: "#1d4ed8", bg: "#eff6ff" },
};

const STATUS_MAP = {
    Active: { label: "Active", color: "#d97706", bg: "#fffbeb" },
    Pending: { label: "Pending", color: "#0f766e", bg: "#ccfbf1" },
    Accepted: { label: "Accepted", color: "#2563eb", bg: "#dbeafe" },
    Started: { label: "Started", color: "#16a34a", bg: "#f0fdf4" },
    InProgress: { label: "In Progress", color: "#16a34a", bg: "#f0fdf4" },
    Completed: { label: "Completed", color: "#475569", bg: "#f1f5f9" },
    Ended: { label: "Completed", color: "#475569", bg: "#f1f5f9" }, // backend calls it Ended
    Cancelled: { label: "Cancelled", color: "#dc2626", bg: "#fef2f2" },
    Scheduled: { label: "Scheduled", color: "#0ea5e9", bg: "#e0f2fe" },
    pending: { label: "Pending", color: "#0f766e", bg: "#ccfbf1" },
    accepted: { label: "Accepted", color: "#2563eb", bg: "#dbeafe" },
    started: { label: "Started", color: "#16a34a", bg: "#f0fdf4" },
    inprogress: { label: "In Progress", color: "#16a34a", bg: "#f0fdf4" },
    completed: { label: "Completed", color: "#475569", bg: "#f1f5f9" },
    ended: { label: "Completed", color: "#475569", bg: "#f1f5f9" },
    cancelled: { label: "Cancelled", color: "#dc2626", bg: "#fef2f2" },
    scheduled: { label: "Scheduled", color: "#0ea5e9", bg: "#e0f2fe" },
};

function formatDate(dateStr) {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatTime(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}

function formatPrice(price) {
    if (!price && price !== 0) return "—";
    const num = parseFloat(price);
    if (isNaN(num)) return "—";
    return `${num.toFixed(2)} ETB`;
}

export default function PassengerTrips() {
    const navigate = useNavigate();
    const passenger = getPassengerData();
    const token = getPassengerToken();
    const passengerId = passenger?.id;

    const [trips, setTrips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!passengerId || !token) return;
        let cancelled = false;

        (async () => {
            try {
                setLoading(true);
                setError(null);
                const headers = { Authorization: `Bearer ${token}` };
                const [tripsRes, reservationsRes] = await Promise.allSettled([
                    axios.get(
                        `${process.env.REACT_APP_BASE_URL}/passengerTrips/${passengerId}`,
                        { headers }
                    ),
                    axios.get(
                        `${process.env.REACT_APP_BASE_URL}/passengerReservation/${passengerId}`,
                        { headers }
                    ),
                ]);
                if (tripsRes.status !== "fulfilled") {
                    throw tripsRes.reason;
                }
                const tripsData = Array.isArray(tripsRes.value.data) ? tripsRes.value.data : [];
                const reservationPayload = reservationsRes.status === "fulfilled"
                    ? (reservationsRes.value?.data?.reservation ?? reservationsRes.value?.data ?? [])
                    : [];
                const reservations = Array.isArray(reservationPayload) ? reservationPayload : [];
                const normalizedReservations = reservations.map((r) => {
                    const driver = r.Driver || r.driver || r.driver2 || null;
                    const drivers = driver
                        ? [{
                            id: driver.id,
                            firstName: driver.firstName,
                            middleName: driver.middleName,
                            lastName: driver.lastName,
                            phoneNumber: driver.phoneNumber,
                        }]
                        : [];
                    return {
                        ...r,
                        id: `reservation-${r.id}`,
                        reservationId: r.id,
                        isReservation: true,
                        pickUpTime: r.pickUpDate || r.pickUpTime,
                        createdAt: r.createdAt || r.pickUpDate,
                        updatedAt: r.updatedAt || r.pickUpDate,
                        drivers,
                    };
                });

                const merged = [...tripsData, ...normalizedReservations];
                merged.sort((a, b) => {
                    const ta = new Date(a.updatedAt || a.createdAt || a.pickUpTime || a.pickUpDate || 0).getTime();
                    const tb = new Date(b.updatedAt || b.createdAt || b.pickUpTime || b.pickUpDate || 0).getTime();
                    if (tb !== ta) return tb - ta;
                    return (String(b.id) || "").localeCompare(String(a.id) || "");
                });

                if (!cancelled) {
                    setTrips(merged);
                }
            } catch (err) {
                if (!cancelled) {
                    setError("Failed to load trip history. Please try again.");
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [passengerId, token]);

    const DONE_STATUSES = ["Completed", "Ended", "completed", "ended"];

    const totalSpent = trips
        .filter((t) => DONE_STATUSES.includes(t.status))
        .reduce((sum, t) => {
            const p = parseFloat(t.actualPrice || t.currentPrice || t.estimatedPrice || 0);
            return sum + (isNaN(p) ? 0 : p);
        }, 0);

    const completedCount = trips.filter((t) => DONE_STATUSES.includes(t.status)).length;

    return (
        <div className="pax-trips-wrapper">
            {/* Page Header */}
            <div className="pax-page-header">
                <button className="pax-back-btn" onClick={() => navigate("/passenger")}>
                    ← Back
                </button>
                <div>
                    <h1 className="pax-page-title">Trip History</h1>
                    <p className="pax-page-subtitle">All your past and ongoing trips</p>
                </div>
            </div>

            {/* Summary cards */}
            {!loading && !error && trips.length > 0 && (
                <div className="pax-trips-summary">
                    <div className="pax-summary-card">
                        <div className="pax-summary-icon" style={{ background: "#eff6ff", color: "#1d4ed8" }}>
                            <Car size={20} />
                        </div>
                        <div>
                            <div className="pax-summary-value">{trips.length}</div>
                            <div className="pax-summary-label">Total Trips</div>
                        </div>
                    </div>
                    <div className="pax-summary-card">
                        <div className="pax-summary-icon" style={{ background: "#f0fdf4", color: "#16a34a" }}>
                            <CheckCircle size={20} />
                        </div>
                        <div>
                            <div className="pax-summary-value">{completedCount}</div>
                            <div className="pax-summary-label">Completed</div>
                        </div>
                    </div>
                    <div className="pax-summary-card">
                        <div className="pax-summary-icon" style={{ background: "#fffbeb", color: "#d97706" }}>
                            <DollarSign size={20} />
                        </div>
                        <div>
                            <div className="pax-summary-value">{totalSpent.toFixed(0)} ETB</div>
                            <div className="pax-summary-label">Total Spent</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Loading */}
            {loading && (
                <div className="pax-card" style={{ display: "flex", alignItems: "center", gap: 14, color: "#64748b" }}>
                    <Loader size={22} style={{ animation: "pax-spin 1s linear infinite" }} />
                    <span>Loading your trip history…</span>
                </div>
            )}

            {/* Error */}
            {error && !loading && (
                <div className="pax-status-panel cancelled">
                    <div className="pax-status-header">
                        <div className="pax-status-icon"><AlertCircle size={22} /></div>
                        <div>
                            <p className="pax-status-title">Could not load trips</p>
                            <p className="pax-status-sub">{error}</p>
                        </div>
                    </div>
                    <button className="pax-btn-primary" onClick={() => window.location.reload()}>
                        Retry
                    </button>
                </div>
            )}

            {/* Empty state */}
            {!loading && !error && trips.length === 0 && (
                <div className="pax-trips-empty">
                    <div className="pax-empty-icon">
                        <Car size={40} color="#94a3b8" />
                    </div>
                    <h3>No trips yet</h3>
                    <p>Once you book your first ride, emergency, or home care service, it will appear here.</p>
                    <button className="pax-btn-primary" onClick={() => navigate("/passenger")}>
                        Book a Service <ArrowRight size={16} />
                    </button>
                </div>
            )}

            {/* Trip list */}
            {!loading && !error && trips.length > 0 && (
                <div className="pax-trips-list">
                    {trips.map((trip) => {
                        const isEmergency = trip.isEmergency || trip.merged;
                        const isReservation = trip.isReservation;
                        const svcType =
                            isReservation
                                ? SERVICE_TYPE_MAP.Scheduled
                                : isEmergency
                                ? SERVICE_TYPE_MAP.Emergency
                                : trip.homeCare
                                    ? SERVICE_TYPE_MAP.HomeCare
                                    : SERVICE_TYPE_MAP.default;

                        const statusInfo = STATUS_MAP[trip.status] || { label: trip.status || "Unknown", color: "#64748b", bg: "#f8fafc" };

                        const driverList = trip.drivers?.length > 0
                            ? trip.drivers
                            : trip.Driver
                                ? [trip.Driver]
                                : [];

                        const price = trip.actualPrice || trip.currentPrice || trip.estimatedPrice;

                        return (
                            <div className="pax-trip-card" key={trip.id || trip.mergedTripIds?.[0]}>
                                {/* Card header */}
                                <div className="pax-trip-card-header">
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        <div
                                            className="pax-trip-type-icon"
                                            style={{ background: svcType.bg, color: svcType.color }}
                                        >
                                            {svcType.icon}
                                        </div>
                                        <div>
                                            <div className="pax-trip-type-label">{svcType.label}</div>
                                            <div className="pax-trip-date">
                                                {formatDate(trip.pickUpTime || trip.createdAt)} &middot; {formatTime(trip.pickUpTime || trip.createdAt)}
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                                        <span
                                            className="pax-trip-status-badge"
                                            style={{ background: statusInfo.bg, color: statusInfo.color }}
                                        >
                                            {statusInfo.label}
                                        </span>
                                        {price && (
                                            <span className="pax-trip-price">{formatPrice(price)}</span>
                                        )}
                                    </div>
                                </div>

                                {/* Route */}
                                <div className="pax-trip-route">
                                    <div className="pax-trip-location">
                                        <span className="pax-loc-dot pickup" />
                                        <span className="pax-loc-text">{trip.pickUpPlace || "Pickup location"}</span>
                                    </div>
                                    {(trip.destinationPlace || trip.destination) && (
                                        <div className="pax-trip-location">
                                            <span className="pax-loc-dot destination" />
                                            <span className="pax-loc-text">{trip.destinationPlace || trip.destination}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Driver info */}
                                {driverList.length > 0 && (
                                    <div className="pax-trip-driver-row">
                                        {driverList.map((d, idx) => (
                                            <div key={d?.id || idx} className="pax-trip-driver-chip">
                                                <User size={13} />
                                                {d?.firstName} {d?.lastName}
                                                {d?.phoneNumber && <span style={{ color: "#94a3b8" }}> · {d.phoneNumber}</span>}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Rating */}
                                {(trip.rating !== null && trip.rating !== undefined) && (
                                    <div className="pax-trip-rating">
                                        <Star size={13} color="#f59e0b" fill="#f59e0b" />
                                        <span>{trip.rating}/5</span>
                                        {trip.feedback && <span className="pax-trip-feedback">"{trip.feedback}"</span>}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
